import {
  Heading,
  HStack,
  Icon,
  useDisclosure,
  VStack,
  Separator,
} from '@chakra-ui/react';
import {
  QuoteStatusEnum,
  RecordShareEngagementAction,
  RecordShareEntity,
} from '@monetize/types/app';
import { toDateShort } from '@monetize/utils/core';
import { useEffect, useState } from 'react';
import { FaRegClock } from 'react-icons/fa';
import { FaBuilding } from 'react-icons/fa6';
import {
  MdCreditCard,
  MdOutlineBusiness,
  MdOutlinePeople,
} from 'react-icons/md';
import { handleApiErrorToast } from '../../api/axios';
import {
  useAcceptQuoteForQuoteShare,
  useTrackRecordShareAction,
} from '../../api/shareService';
import AppLoading from '../../components/App/AppLoading';
import { MBox, MButton, MFlex, MLink, MText } from '../../components/Monetize';
import { PreviewPdfCard } from '../../components/Monetize/PreviewPdfAccordion/PreviewPdfCard';
import { StripeWrapper } from '../../components/PaymentMethods/Form/StripeWrapper';
import { PAPER_PAGE_MAX_WIDTH } from '../../constants/quotes';
import { useConfirmModal } from '../../services/confirmModal';
import { useFlags } from '../../services/launchDarkly';
import { logger } from '../../services/logger';
import Sentry from '../../services/sentry';
import {
  getRecordShareCaptureContext,
  getRecordShareErrorLogContext,
  getRecordShareLogContext,
} from '../../utils/recordShareLogging';
import { QuoteShareCompanyDetails } from './companyDetails/QuoteShareCompanyDetails';
import { QuoteShareCompanyDetailsEditModal } from './companyDetails/QuoteShareCompanyDetailsEditModal';
import { QuoteShareContactCard } from './contacts/QuoteShareContactCard';
import { QuoteShareContactEditModal } from './contacts/QuoteShareContactEditModal';
import { QuoteShareEsignContact } from './contacts/QuoteShareEsignContact';
import { QuoteSharePaymentMethod } from './paymentMethod/QuoteSharePaymentMethod';
import { QuoteSharePaymentMethodEditModal } from './paymentMethod/QuoteSharePaymentMethodEditModal';
import {
  getEsignRecipientMessage,
  isMissingRequiredAddress,
  resolveQuoteShareCompanyDetails,
} from './quote-share.utils';
import { QuoteShareButtonCard } from './QuoteShareButtonCard';
import { QuoteShareStatusBanner } from './QuoteShareStatusBanner';
import { useQuoteShareData } from './useQuoteShareData';

function isValidRedirectUrl(url: string | null | undefined): url is string {
  if (!url?.trim()) {
    return false;
  }
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

export const QuoteSharePage = () => {
  const {
    isLoading,
    isLoadingPdf,
    stripeKeys,
    hasPaymentGatewayConfigured,
    quoteData,
    quoteDataHasError,
    quotePdfData,
    quotePdfHasError,
    recordShareAuthData,
    quoteShareUxConfig,
    lockPaymentMethod,
    paymentType,
    setPaymentType,
    selectedPaymentMethodId,
    setSelectedPaymentMethodId,
    refreshSetupIntent,
  } = useQuoteShareData();

  const {
    showConfirmModal,
    setModalLoading: setConfirmationModalLoading,
    closeModal: closeConfirmationModal,
  } = useConfirmModal();

  const isDisabled = quoteData?.quote.status !== QuoteStatusEnum.APPROVED;

  const {
    open: companyDetailsModalIsOpen,
    onClose: onCompanyDetailsModalClose,
    onOpen: onCompanyDetailsModalOpen,
  } = useDisclosure();

  const {
    open: contactModalIsOpen,
    onClose: onContactModalClose,
    onOpen: onContactModalOpen,
  } = useDisclosure();

  const {
    open: paymentModalIsOpen,
    onClose: onPaymentModalClose,
    onOpen: onPaymentModalOpen,
  } = useDisclosure();

  const { tempDescopeQuoteSharePayment } = useFlags();

  const { mutateAsync: acceptAndProcessQuote, isPending: isSaveLoading } =
    useAcceptQuoteForQuoteShare();

  const { mutate: trackRecordShareAction } = useTrackRecordShareAction();

  const [pendingRedirectUrl, setPendingRedirectUrl] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (quoteData) {
      // Token/tokenLength are logged on the success path too so a healthy load
      // is directly comparable to a failed one.
      Sentry.logger.info('QuoteSharePage accessed', {
        ...getRecordShareLogContext(recordShareAuthData),
        quoteId: quoteData.quote.id,
        quoteStatus: quoteData.quote.status,
        requirePaymentMethod: String(requirePaymentMethod),
        requireContactAddress: String(requireContactAddress),
        hasPaymentGateway: String(hasPaymentGatewayConfigured),
        requiresEsign: String(!!quoteData.quote.requiresEsign),
        hasRedirectUrl: String(!!quoteData.redirectUrl),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteData?.quote?.id]);

  useEffect(() => {
    if (pendingRedirectUrl) {
      const timeout = setTimeout(() => {
        window.location.href = pendingRedirectUrl;
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [pendingRedirectUrl]);

  /**
   * TEMPORARY SOLUTION
   * We will solve this with rules-based configuration - for now, hard-coding quote name to not require payment method
   */
  const requirePaymentMethodNotRequiredOverride =
    !!tempDescopeQuoteSharePayment?.enabled &&
    !!tempDescopeQuoteSharePayment.quoteNameContains &&
    !!quoteData?.quote?.description?.includes(
      tempDescopeQuoteSharePayment.quoteNameContains,
    );

  const requirePaymentMethod =
    !requirePaymentMethodNotRequiredOverride &&
    !!quoteShareUxConfig.requirePaymentMethod;

  const requireContactAddress = !!quoteShareUxConfig.requireContactAddress;

  const isMissingRequiredContactInfo =
    requireContactAddress && isMissingRequiredAddress(quoteData?.quote);

  const isMissingRequiredPaymentInfo =
    paymentType === 'ELECTRONIC' &&
    requirePaymentMethod &&
    selectedPaymentMethodId === null;

  const companyDetails = resolveQuoteShareCompanyDetails({
    allowAccountNameUpdate: quoteShareUxConfig.allowAccountNameUpdate,
    poVisibility: quoteShareUxConfig.purchaseOrderNumberVisibility,
    vatVisibility: quoteShareUxConfig.vatNumberVisibility,
    allowPoNumber: !!quoteData?.allowPoNumber,
    allowVatNumber: !!quoteData?.allowVatNumber,
    purchaseOrderNumber: quoteData?.quote.purchaseOrderNumber,
    vatNumber: quoteData?.quote.vatNumber,
  });

  async function handleAcceptQuote() {
    if (!quoteData) {
      return;
    }

    const onYes = async () => {
      try {
        setConfirmationModalLoading(true);

        const response = await acceptAndProcessQuote({
          authParams: recordShareAuthData,
          paymentMethodId:
            paymentType === 'ELECTRONIC' ? selectedPaymentMethodId : undefined,
          collectUpfrontPayment:
            paymentType === 'ELECTRONIC'
              ? quoteShareUxConfig.collectPaymentBeforeQuoteProcessing
              : undefined,
        });

        Sentry.logger.info('QuoteSharePage - quote accepted', {
          quoteId: recordShareAuthData.recordId,
          tenantId: recordShareAuthData.tenantId,
        });

        trackRecordShareAction({
          action: RecordShareEngagementAction.ACCEPTED_QUOTE,
          entity: RecordShareEntity.QUOTE,
          recordId: recordShareAuthData.recordId,
          tenantId: recordShareAuthData.tenantId,
          token: recordShareAuthData.token,
        });

        const redirectUrl =
          response?.redirectUrl ?? quoteData?.redirectUrl ?? undefined;
        if (isValidRedirectUrl(redirectUrl)) {
          Sentry.logger.info('QuoteSharePage - redirect initiated', {
            quoteId: recordShareAuthData.recordId,
            tenantId: recordShareAuthData.tenantId,
            redirectUrl,
          });
          setConfirmationModalLoading(false);
          closeConfirmationModal();
          setPendingRedirectUrl(redirectUrl);
          return;
        }
        Sentry.logger.warn(
          'QuoteSharePage - quote accepted but redirect skipped (no valid redirect url)',
          {
            ...getRecordShareLogContext(recordShareAuthData),
            quoteId: recordShareAuthData.recordId,
            responseRedirectUrl: response?.redirectUrl ?? null,
            quoteDataRedirectUrl: quoteData?.redirectUrl ?? null,
            resolvedRedirectUrl: redirectUrl ?? null,
          },
        );
        setConfirmationModalLoading(false);
        closeConfirmationModal();
      } catch (ex) {
        const logContext = getRecordShareErrorLogContext(
          recordShareAuthData,
          ex,
        );
        Sentry.logger.error('QuoteSharePage - quote acceptance failed', {
          ...logContext,
          quoteId: recordShareAuthData.recordId,
          quoteStatus: quoteData.quote.status,
          paymentType,
          hasSelectedPaymentMethod: String(!!selectedPaymentMethodId),
        });
        setConfirmationModalLoading(false);
        Sentry.captureException(
          ex,
          getRecordShareCaptureContext(logContext, {
            type: 'QUOTE_SHARE',
            action: 'ACCEPT_QUOTE',
            quoteId: recordShareAuthData.recordId,
          }),
        );
        handleApiErrorToast(ex);
        logger.error(ex);
      }
    };

    showConfirmModal({
      title: `Confirmation`,
      content: (
        <MBox>
          <MText>
            By accepting, you agree to the terms outlined in the quote document.
          </MText>
          {quoteData.quote.requiresEsign && (
            <MText mt={1}>
              {getEsignRecipientMessage(quoteData.quote.externalEsignContact)}
            </MText>
          )}
        </MBox>
      ),
      onYes,
      yesBtnProps: {
        variant: 'primary',
      },
      noBtnProps: {
        variant: 'cancel',
      },
      yesButton: 'Accept',
      noButton: 'Cancel',
    });
  }

  if (pendingRedirectUrl) {
    return (
      <MBox
        bg="tGray.light1"
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <MBox textAlign="center" maxW="lg" p={8}>
          <MText fontSize="xl" fontWeight="bold" mb={4}>
            Redirecting...
          </MText>
          <MText>
            You are being redirected. If the page does not load automatically,{' '}
            <MLink
              href={pendingRedirectUrl}
              color="tBlue.base"
              fontWeight="600"
            >
              click here to continue
            </MLink>
            .
          </MText>
        </MBox>
      </MBox>
    );
  }

  if (isLoading || !quoteData) {
    return <AppLoading />;
  }

  // Errors are handled in useQuoteShareData and displayed by the layout wrapper via query params
  if (quoteDataHasError || quotePdfHasError) {
    return null;
  }

  const areShippingAndBillingContactsSame =
    quoteData.quote.shippingContact?.id === quoteData.quote.billingContact?.id;

  return (
    <MBox bg="tGray.light1" minH="100vh">
      <MBox as="header" bg="tBlue.base">
        <MBox px={[6, 12]} py={[4, 8]}>
          <MBox maxW="72rem" mx="auto">
            <VStack gap={0} alignItems="start">
              <MText
                color="tGray.lightGray"
                textTransform="uppercase"
                letterSpacing="0.10em"
                fontWeight={500}
              >
                Quote Prepared For
              </MText>
              <Heading as="h1" color="tWhite.text" fontSize="4xl">
                {quoteData.quote.accountName}
              </Heading>
              <Separator borderColor="tGray.darkPurple" my={[2, 4]} />
              <HStack gap={6} wrap="wrap">
                <MFlex gap={2} alignItems="center">
                  <Icon
                    boxSize="1.5rem"
                    color="tGray.light"
                    width={4}
                    height={4}
                    asChild
                  >
                    <FaRegClock />
                  </Icon>
                  <MBox as="span" color="tGray.light">
                    Expires
                  </MBox>
                  <MBox as="span" color="tWhite.text" fontWeight={500}>
                    {toDateShort(quoteData.quote.expirationDate)}
                  </MBox>
                </MFlex>
                <MFlex gap={2} alignItems="center">
                  <Icon
                    boxSize="1.5rem"
                    color="tGray.light"
                    width={4}
                    height={4}
                    asChild
                  >
                    <FaBuilding />
                  </Icon>
                  <MBox as="span" color="tGray.light">
                    From
                  </MBox>
                  <MBox as="span" color="tWhite.text" fontWeight={500}>
                    {quoteData.quote.fromCompany.name}
                  </MBox>
                </MFlex>
              </HStack>
            </VStack>
          </MBox>
        </MBox>
      </MBox>
      <MFlex
        width="100%"
        maxW={PAPER_PAGE_MAX_WIDTH}
        flexDirection="column"
        pb="10"
        px="12"
        alignItems="center"
        justifySelf="center"
      >
        <QuoteShareStatusBanner quote={quoteData.quote} />

        <HStack alignItems="start" gap={6} w="100%" mt={6} wrap="wrap-reverse">
          {/* Make this a page by page layout so it generally fits on the screen */}
          <PreviewPdfCard
            isLoading={isLoadingPdf}
            downloadFileName={
              quoteData.quote.description?.trim() ||
              `quote-${quoteData.quote.id}`
            }
            pdfData={quotePdfData}
            maxWidth={{ base: '100%', lg: 'calc(65% - 0.75rem)' }}
          />
          <VStack gap={6} w={{ base: '100%', lg: 'calc(35% - 0.75rem)' }}>
            {!isDisabled && (
              <MButton
                variant="primary"
                borderRadius="xl"
                p={6}
                fontSize="md"
                w="100%"
                type="button"
                disabled={
                  isMissingRequiredContactInfo ||
                  isMissingRequiredPaymentInfo ||
                  companyDetails.isMissingRequiredPo ||
                  companyDetails.isMissingRequiredVat ||
                  isSaveLoading
                }
                onClick={() => handleAcceptQuote()}
              >
                Accept Quote
              </MButton>
            )}

            {quoteData.quote.requiresEsign && !isDisabled && (
              <MText mt={1}>
                {getEsignRecipientMessage(quoteData.quote.externalEsignContact)}
              </MText>
            )}

            {companyDetails.showCard && (
              <QuoteShareButtonCard
                title="Company and Order Details"
                icon={MdOutlineBusiness}
                props={{ cardProps: { w: '100%', borderRadius: 'xl' } }}
                disabled={isDisabled || !companyDetails.cardEditable}
                onClick={() => onCompanyDetailsModalOpen()}
              >
                <QuoteShareCompanyDetails
                  showAccountName={companyDetails.showAccountName}
                  showPo={companyDetails.showPo}
                  showVat={companyDetails.showVat}
                  accountName={quoteData.quote.accountName}
                  purchaseOrderNumber={quoteData.quote.purchaseOrderNumber}
                  vatNumber={quoteData.quote.vatNumber}
                  isMissingRequiredPo={companyDetails.isMissingRequiredPo}
                  isMissingRequiredVat={companyDetails.isMissingRequiredVat}
                />
              </QuoteShareButtonCard>
            )}

            {companyDetails.cardEditable && companyDetailsModalIsOpen && (
              <QuoteShareCompanyDetailsEditModal
                quoteData={quoteData}
                authParams={recordShareAuthData}
                accountNameEditable={companyDetails.accountNameEditable}
                poEditable={companyDetails.poEditable}
                vatEditable={companyDetails.vatEditable}
                poRequired={companyDetails.poRequired}
                vatRequired={companyDetails.vatRequired}
                onClose={onCompanyDetailsModalClose}
              />
            )}

            <QuoteShareButtonCard
              title="Billing & Shipping Details"
              icon={MdOutlinePeople}
              props={{ cardProps: { w: '100%', borderRadius: 'xl' } }}
              disabled={isDisabled}
              onClick={() => onContactModalOpen()}
            >
              <QuoteShareContactCard
                label={
                  areShippingAndBillingContactsSame
                    ? 'Billing & Shipping Contact'
                    : 'Billing'
                }
                contact={quoteData.quote.billingContact}
                address={quoteData.quote.billingAddress}
                addressSource={quoteData.quote.addressSource}
              />
              {!areShippingAndBillingContactsSame && (
                <QuoteShareContactCard
                  label="Shipping"
                  contact={quoteData.quote.shippingContact}
                  address={quoteData.quote.shippingAddress}
                  addressSource={quoteData.quote.addressSource}
                />
              )}

              {quoteData.quote.requiresEsign &&
                quoteData.quote.externalEsignContact && (
                  <QuoteShareEsignContact
                    contact={quoteData.quote.externalEsignContact}
                  />
                )}

              {isMissingRequiredContactInfo && (
                <MText color="tOrange.base" fontWeight={500}>
                  Please provide your address details to continue.
                </MText>
              )}
            </QuoteShareButtonCard>

            {contactModalIsOpen && (
              <QuoteShareContactEditModal
                requireAddress={requireContactAddress}
                quoteData={quoteData}
                authParams={recordShareAuthData}
                onClose={onContactModalClose}
              />
            )}

            {hasPaymentGatewayConfigured && requirePaymentMethod && (
              <QuoteShareButtonCard
                title="Payment Method"
                icon={MdCreditCard}
                props={{ cardProps: { w: '100%', borderRadius: 'xl' } }}
                disabled={isDisabled || lockPaymentMethod}
                onClick={() => onPaymentModalOpen()}
              >
                <QuoteSharePaymentMethod
                  paymentMethods={quoteData.paymentMethods}
                  selectedPaymentMethodId={selectedPaymentMethodId}
                  paymentType={paymentType}
                  quoteShareUxConfig={quoteShareUxConfig}
                />
              </QuoteShareButtonCard>
            )}

            {paymentModalIsOpen && stripeKeys && (
              <StripeWrapper
                apiKey={stripeKeys.publicKey}
                clientSecret={stripeKeys.clientSecret}
              >
                <QuoteSharePaymentMethodEditModal
                  companyName={quoteData.quote.fromCompany.name ?? ''}
                  quoteData={quoteData}
                  authParams={recordShareAuthData}
                  stripePublicKey={stripeKeys.publicKey}
                  quoteShareUxConfig={quoteShareUxConfig}
                  paymentType={paymentType}
                  setPaymentType={setPaymentType}
                  onSave={setSelectedPaymentMethodId}
                  onPaymentMethodCreated={refreshSetupIntent}
                  onClose={onPaymentModalClose}
                />
              </StripeWrapper>
            )}
          </VStack>
        </HStack>
      </MFlex>
      <MBox as="footer" bg="tGray.shareFooter">
        <MBox px={[6, 12]} py={[8, 12]}>
          <MBox maxW="72rem" mx="auto">
            <MText color="tGray.darkPurple" textAlign="center">
              By accepting this quote, you agree to the terms and conditions
              outlined in the document above. This quote is valid until the
              expiration date shown.
            </MText>
            <Separator borderColor="tGray.lightPurple" my={[4, 6]} />
            <MText color="tGray.darkPurple" textAlign="center">
              Powered by{' '}
              <MLink
                href="https://www.monetizenow.ai"
                target="_blank"
                color="inherit"
                fontWeight="600"
              >
                MonetizeNow
              </MLink>
            </MText>
          </MBox>
        </MBox>
      </MBox>
    </MBox>
  );
};
